import { FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import cn from 'classnames';
import dibsCss from 'dibs-css';
import { FormattedMessage, useIntl } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link';
import BankAccountIcon from 'dibs-icons/exports/legacy/BankAccount';
import { type AccountType } from 'dibs-ach-form/exports/ACHForm';
import { type PaymentMethod, isPaymentProcessor } from '../../types';
import { PaymentMethodSelectOption } from '../PaymentMethodSelectOption';
import { messages } from './messages';
import { usePaymentMethodSelectContext } from '../PaymentMethodSelectContext';
import { AchPaymentForm } from '../ach/AchPaymentForm';

import { WalletAchOption_ach$key } from './__generated__/WalletAchOption_ach.graphql';

const achFragment = graphql`
    fragment WalletAchOption_ach on ACH {
        token
        accountType
        routingNumber
        last4
        paymentProcessor
    }
`;

type Props = {
    name: string;
    isChecked: boolean;
    dataTn: string;
    ach: WalletAchOption_ach$key;
    isPermissible: boolean;
    onChange: (paymentMethod: PaymentMethod) => void;
    onDelete: (paymentMethod: PaymentMethod) => void;
};

export const WalletAchOption: FC<Props> = props => {
    const { isChecked, dataTn, name, onChange, onDelete, ach: achRef, isPermissible } = props;

    const intl = useIntl();
    const achMethod = useFragment(achFragment, achRef) || {};

    const { updateAchForm } = usePaymentMethodSelectContext();

    const { token, paymentProcessor, accountType, routingNumber, last4 } = achMethod;

    if (!isPermissible && paymentProcessor === 'STRIPE') {
        return null;
    }

    function handleSelect(): void {
        if (token && isPaymentProcessor(paymentProcessor)) {
            onChange({
                paymentMethodType: 'ACH',
                paymentMethodToken: token,
                paymentProcessor,
            });

            if (paymentProcessor === 'BRAINTREE' && !isPermissible) {
                updateAchForm({
                    isVerifying: true,
                    accountType: (accountType as AccountType) || '',
                    routingNumber: routingNumber || '',
                });
            }
        }
    }

    function handleDeleteClick(): void {
        if (token && isPaymentProcessor(paymentProcessor)) {
            onDelete({
                paymentMethodType: 'ACH',
                paymentMethodToken: token,
                paymentProcessor,
            });
        }
    }

    return (
        <PaymentMethodSelectOption
            isChecked={isChecked}
            dataTn={dataTn}
            name={name}
            onChange={handleSelect}
            value={token || ''}
            icon={<BankAccountIcon className={dibsCss.block} />}
            label={
                <>
                    <div className={cn(dibsCss.flexGrow, dibsCss.flex, dibsCss.flexCol)}>
                        <span className={dibsCss.sassyFontBodyMediumHeavy}>
                            <FormattedMessage
                                id="dibs-payment.WalletAchOption.bankAccountType"
                                defaultMessage="{accountType, select,
                                    checking {Checking ending in {last4}}
                                    savings {Savings ending in {last4}}
                                    other {Account ending in {last4}}
                                }"
                                values={{ accountType, last4 }}
                            />
                        </span>
                        <span className={dibsCss.sassyFontBodySmall}>
                            <FormattedMessage
                                id="dibs-payment.WalletAchOption.bankAccount"
                                defaultMessage="Bank Account"
                            />
                        </span>
                    </div>
                    <div>
                        <Link onClick={handleDeleteClick}>
                            {intl.formatMessage(messages.deleteButton)}
                        </Link>
                    </div>
                </>
            }
            content={
                isChecked && !isPermissible && paymentProcessor === 'BRAINTREE' ? (
                    <div className={cn(dibsCss.mtSmall, dibsCss.mbSmall)}>
                        <p>
                            <span className={dibsCss.uppercase}>
                                <FormattedMessage
                                    id="dibs-payment.WalletAchOption.verifyBankAccountHeader"
                                    defaultMessage="Please verify your account"
                                />
                            </span>
                            <span className={cn(dibsCss.plXsmall, dibsCss.prXsmall)}>-</span>
                            <span>
                                <FormattedMessage
                                    id="cdibs-payment.WalletAchOption.verifyCardDigits"
                                    defaultMessage="ending in {lastDigits}"
                                    values={{ lastDigits: last4 }}
                                />
                            </span>
                        </p>

                        <p className={dibsCss.mtXsmall}>
                            <FormattedMessage
                                id="dibs-payment.WalletAchOption.verifyCardText"
                                defaultMessage="For security purposes, we require you to re-enter your bank account number, name, and billing address for each new shipping address."
                            />
                        </p>

                        <div className={dibsCss.mtXsmall}>
                            <AchPaymentForm disabledFields={['accountType', 'routingNumber']} />
                        </div>
                    </div>
                ) : null
            }
        />
    );
};
